/**
 * Category Selector Component
 *
 * A horizontal row of category buttons for selecting entry categories.
 * Uses category colors from the design system.
 */

import { Briefcase, Heart, Palette, Users, Home, BookOpen } from 'lucide-react';
import { CATEGORY_META, type Category } from '@have-done/shared';

const CATEGORY_ICONS = {
  work: Briefcase,
  health: Heart,
  creative: Palette,
  social: Users,
  personal: Home,
  learning: BookOpen,
} as const;

const CATEGORY_COLORS = {
  work: 'bg-category-work/20 border-category-work text-category-work hover:bg-category-work/30',
  health: 'bg-category-health/20 border-category-health text-category-health hover:bg-category-health/30',
  creative: 'bg-category-creative/20 border-category-creative text-category-creative hover:bg-category-creative/30',
  social: 'bg-category-social/20 border-category-social text-category-social hover:bg-category-social/30',
  personal: 'bg-category-personal/20 border-category-personal text-category-personal hover:bg-category-personal/30',
  learning: 'bg-category-learning/20 border-category-learning text-amber-700 hover:bg-category-learning/30',
} as const;

const CATEGORY_COLORS_SELECTED = {
  work: 'bg-category-work border-category-work text-white',
  health: 'bg-category-health border-category-health text-white',
  creative: 'bg-category-creative border-category-creative text-white',
  social: 'bg-category-social border-category-social text-white',
  personal: 'bg-category-personal border-category-personal text-white',
  learning: 'bg-category-learning border-category-learning text-text-primary',
} as const;

interface CategorySelectorProps {
  value: Category | null;
  onChange: (category: Category) => void;
  disabled?: boolean;
}

export function CategorySelector({ value, onChange, disabled }: CategorySelectorProps) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Select category">
      {(Object.keys(CATEGORY_META) as Category[]).map((category) => {
        const meta = CATEGORY_META[category];
        const Icon = CATEGORY_ICONS[category];
        const isSelected = value === category;

        return (
          <button
            key={category}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(category)}
            className={`
              inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5
              text-sm font-medium transition-colors min-h-touch
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isSelected ? CATEGORY_COLORS_SELECTED[category] : CATEGORY_COLORS[category]}
            `}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}
